'use client';

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { FAMILIES, rangeLook, type DoorCatalogue, type DoorColour, type DoorRange } from '../data/doorCatalogue';
import { doorSvg } from '../drawing/door';
import { SvgArt } from '../drawing/SvgArt';

/**
 * Step 1 of the door builder: the chosen range, a few quick picks, and a
 * dialog to browse every range with filters (family, finish, collection,
 * search, sort). Built for any number of ranges: the dialog filters and sorts
 * in place; only the quick picks are a fixed handful.
 */
export type RangeFilter = { family: string; q: string; finish: string; collection: string; sort: 'pop' | 'name' | 'colours' };

export const EMPTY_FILTER: RangeFilter = { family: 'all', q: '', finish: '', collection: '', sort: 'pop' };

export function filterRanges(ranges: DoorRange[], f: RangeFilter): DoorRange[] {
  const q = f.q.trim().toLowerCase();
  let list = ranges.filter(
    (r) =>
      (f.family === 'all' || r.family === f.family || (f.family === 'wardrobe' && r.wardrobe)) &&
      (!f.collection || r.collection === f.collection) &&
      (!f.finish || r.finish === f.finish) &&
      (!q || `${r.name} ${r.build} ${r.finish} ${r.family}`.toLowerCase().includes(q)),
  );
  if (f.sort === 'name') list = [...list].sort((a, b) => a.name.localeCompare(b.name));
  if (f.sort === 'colours') list = [...list].sort((a, b) => b.colours.length - a.colours.length);
  return list;
}

function colourCount(range: DoorRange): string {
  const n = range.colours.length;
  if (n === 0) return range.ral ? 'Painted to order in any colour' : 'No colours';
  return `${n} colour${n === 1 ? '' : 's'}${range.ral ? ', or painted to order in any colour' : ''}`;
}

function swatchBackground(colour: DoorColour | undefined): string {
  if (!colour) return 'transparent';
  return colour.wood ? 'linear-gradient(135deg,#d4ab7a,#9c7650)' : colour.hex;
}

function RangeThumb({ range, colour, size }: { range: DoorRange; colour: DoorColour | undefined; size: 'card' | 'thumb' }) {
  const id = useId();
  const svg = doorSvg(range.style, colour?.hex ?? '#8a9a82', {
    look: rangeLook(range),
    wood: colour?.wood,
    w: 300,
    h: 500,
    handle: range.noHandle || range.style === 'jpull' ? 'none' : 'bar',
    label: `${range.name} door${colour ? ` in ${colour.name}` : ''}`,
    idPrefix: id,
  });
  return <SvgArt svg={svg} className={size === 'thumb' ? 'sk-db__thumbart' : undefined} />;
}

export interface RangePickerProps {
  catalogue: DoorCatalogue;
  ranges: DoorRange[];
  range: DoorRange;
  colourId: string;
  onPick(rangeId: string): void;
  /** Open the dialog on mount with this family, e.g. from `?family=shaker`. */
  initialFamily?: string;
}

export function RangePicker({ catalogue, ranges, range, colourId, onPick, initialFamily }: RangePickerProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const [filter, setFilter] = useState<RangeFilter>(EMPTY_FILTER);
  const colours = useMemo(() => new Map(catalogue.colours().map((c) => [c.id, c])), [catalogue]);
  const collections = useMemo(() => new Map(catalogue.collections().map((c) => [c.id, c])), [catalogue]);
  const finishes = useMemo(() => [...new Set(ranges.map((r) => r.finish))].sort(), [ranges]);
  const shown = filterRanges(ranges, filter);
  const colourFor = (r: DoorRange) => colours.get(r.colours.includes(colourId) ? colourId : (r.colours[0] as string));

  const open = (family?: string) => {
    if (family) setFilter((f) => ({ ...f, family }));
    dialogRef.current?.showModal();
  };
  useEffect(() => {
    if (initialFamily && FAMILIES.some((f) => f.id === initialFamily)) open(initialFamily);
    // Only on mount: the URL's family opens the dialog once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const quick = ranges.slice(0, 5);
  if (!quick.some((r) => r.id === range.id)) quick.unshift(range);
  const collection = collections.get(range.collection);

  return (
    <>
      <div className="sk-db__current">
        <div className="sk-db__thumb" aria-hidden="true">
          <RangeThumb range={range} colour={colourFor(range)} size="thumb" />
        </div>
        <div>
          {collection && <p className="sk-eyebrow" style={{ fontSize: '.68rem', margin: 0 }}>{`${collection.name} collection`}</p>}
          <h3 className="sk-heading">{range.name}</h3>
          <p>{`${range.build} · ${range.finish}`}</p>
          <p>{colourCount(range)}</p>
        </div>
        <button type="button" className="sk-btn sk-btn--secondary sk-btn--sm" onClick={() => open()} aria-haspopup="dialog">
          {`Browse all ${ranges.length} ranges`}
        </button>
      </div>
      <div className="sk-db__quick" role="group" aria-label="Popular ranges">
        {quick.map((r) => (
          <button key={r.id} type="button" aria-pressed={r.id === range.id} onClick={() => onPick(r.id)}>
            {r.name}
          </button>
        ))}
      </div>

      <dialog
        ref={dialogRef}
        className="sk-db__modal"
        aria-labelledby={titleId}
        onClick={(event) => {
          if (event.target === dialogRef.current) dialogRef.current?.close();
        }}
      >
        <div className="sk-db__mhead">
          <div className="sk-db__mrow">
            <h2 id={titleId} className="sk-heading">
              Choose a door range
            </h2>
            <button type="button" className="sk-db__close" onClick={() => dialogRef.current?.close()}>
              <span aria-hidden="true">×</span>
              <span className="sk-visually-hidden">Close</span>
            </button>
          </div>
          <div className="sk-db__mrow">
            <input
              className="sk-db__input sk-db__msearch"
              type="search"
              placeholder="Search ranges"
              aria-label="Search ranges"
              value={filter.q}
              onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
            />
            <select className="sk-db__input sk-db__mselect" aria-label="Finish" value={filter.finish} onChange={(e) => setFilter((f) => ({ ...f, finish: e.target.value }))}>
              <option value="">Any finish</option>
              {finishes.map((finish) => (
                <option key={finish}>{finish}</option>
              ))}
            </select>
            <select className="sk-db__input sk-db__mselect" aria-label="Collection" value={filter.collection} onChange={(e) => setFilter((f) => ({ ...f, collection: e.target.value }))}>
              <option value="">All collections</option>
              {catalogue.collections().map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <select
              className="sk-db__input sk-db__mselect"
              aria-label="Sort"
              value={filter.sort}
              onChange={(e) => setFilter((f) => ({ ...f, sort: e.target.value as RangeFilter['sort'] }))}
            >
              <option value="pop">Most popular</option>
              <option value="name">Name</option>
              <option value="colours">Most colours</option>
            </select>
            <span className="sk-db__shown" role="status">{`${shown.length} of ${ranges.length} ranges`}</span>
          </div>
          <div className="sk-db__chips" role="group" aria-label="Style">
            {FAMILIES.filter((f) => f.id !== 'wardrobe').map((f) => (
              <button key={f.id} type="button" aria-pressed={filter.family === f.id} onClick={() => setFilter((cur) => ({ ...cur, family: f.id }))}>
                {f.name}
              </button>
            ))}
          </div>
        </div>
        <div className="sk-db__mbody">
          {shown.length === 0 ? (
            <p>
              No ranges match.{' '}
              <button type="button" className="sk-db__mini" onClick={() => setFilter(EMPTY_FILTER)}>
                Clear the filters
              </button>
            </p>
          ) : (
            <ul className="sk-db__rgrid">
              {shown.map((r) => {
                const c = colourFor(r);
                return (
                  <li key={r.id}>
                    <button
                      type="button"
                      className="sk-db__rcard"
                      aria-pressed={r.id === range.id}
                      onClick={() => {
                        onPick(r.id);
                        dialogRef.current?.close();
                      }}
                    >
                      <span className="sk-db__pv" aria-hidden="true">
                        {r.badge && <span className="sk-db__badge">{r.badge}</span>}
                        <RangeThumb range={r} colour={c} size="card" />
                      </span>
                      <span className="sk-db__info">
                        <small className="sk-db__coll">{collections.get(r.collection)?.name}</small>
                        <strong>{r.name}</strong>
                        <small>{r.build}</small>
                        <span className="sk-db__dots" aria-hidden="true">
                          {r.colours.slice(0, 7).map((id) => (
                            <i key={id} style={{ background: swatchBackground(colours.get(id)) }} />
                          ))}
                          <span>{`${r.colours.length}${r.ral ? ' + any' : ''}`}</span>
                        </span>
                        <span className="sk-visually-hidden">{`${colourCount(r)}.`}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </dialog>
    </>
  );
}

export function ColourPicker({
  catalogue,
  range,
  colourId,
  onPick,
  paintColour = '',
  onPaintColour,
}: {
  catalogue: DoorCatalogue;
  range: DoorRange;
  colourId: string;
  onPick(colourId: string): void;
  paintColour?: string;
  onPaintColour?(value: string): void;
}) {
  const paintId = useId();
  const colours = new Map(catalogue.colours().map((c) => [c.id, c]));
  const current = colourId === 'ral' ? { name: 'Any colour, painted to order', finish: 'Name it below' } : colours.get(colourId);
  return (
    <>
      <ul className="sk-db__colours" aria-label="Colours">
        {range.colours.map((id) => {
          const c = colours.get(id);
          if (!c) return null;
          return (
            <li key={id}>
              <button
                type="button"
                className="sk-db__colour"
                aria-pressed={id === colourId}
                aria-label={`${c.name}, ${c.finish}`}
                title={c.name}
                style={{ background: swatchBackground(c) }}
                onClick={() => onPick(id)}
              />
            </li>
          );
        })}
        {range.ral && (
          <li>
            <button
              type="button"
              className="sk-db__colour"
              aria-pressed={colourId === 'ral'}
              aria-label="Any colour, painted to order"
              title="Any colour, painted to order"
              style={{ background: 'conic-gradient(#c0704f,#e3c26b,#8a9a82,#2f5557,#2d3a4f,#6a3238,#c0704f)' }}
              onClick={() => onPick('ral')}
            />
          </li>
        )}
      </ul>
      <p className="sk-db__colourname" aria-live="polite">
        <b>{current?.name}</b>
        {current?.finish ? ` · ${current.finish}` : ''}
      </p>
      {colourId === 'ral' && onPaintColour && (
        <div className="sk-db__field">
          <label className="sk-db__label" htmlFor={paintId}>
            Colour to paint
          </label>
          <input
            id={paintId}
            className="sk-db__input"
            type="text"
            maxLength={60}
            placeholder="e.g. RAL 7016 Anthracite grey, or a paint brand's colour name"
            value={paintColour}
            aria-invalid={paintColour.trim().length < 2}
            onChange={(event) => onPaintColour(event.target.value)}
          />
          <span className="sk-db__hint">Give a RAL or British Standard number, or the paint maker and colour name.</span>
        </div>
      )}
    </>
  );
}
